import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { TaskTrackerBoard, TaskTrackerProject } from '../domain.types'
import { removeBoard } from '../shared/boardCleanup.actions'
import { createInitialProjectsState } from './projects.state.utils'
import {
  createProjectThunk,
  fetchProjectsThunk,
  reorderProjectBoardsThunk,
  reorderProjectsThunk,
} from './projects.thunks'

const sortBoardsByPosition = (boards: TaskTrackerBoard[]) => {
  if (!boards.some((board) => board.position > 0)) return boards

  return [...boards].sort((left, right) => {
    const leftPosition =
      left.position > 0 ? left.position : Number.MAX_SAFE_INTEGER
    const rightPosition =
      right.position > 0 ? right.position : Number.MAX_SAFE_INTEGER

    return leftPosition - rightPosition || left.id - right.id
  })
}

const projectsSlice = createSlice({
  name: 'taskTrackerProjects',
  initialState: createInitialProjectsState(),
  reducers: {
    setActiveProjectId(state, action: PayloadAction<number | null>) {
      state.activeProjectId = action.payload
    },
    clearCreateProjectError(state) {
      state.createProjectError = null
    },
    updateProjectTitle(
      state,
      action: PayloadAction<{ id: number; title: string }>,
    ) {
      const project = state.projects.find((item) => item.id === action.payload.id)
      if (project) {
        project.title = action.payload.title
      }
    },
    removeProject(state, action: PayloadAction<number>) {
      state.projects = state.projects.filter((item) => item.id !== action.payload)
      if (state.activeProjectId === action.payload) {
        state.activeProjectId = null
      }
    },
    addProjectBoard(
      state,
      action: PayloadAction<{ projectId: number; board: TaskTrackerBoard }>,
    ) {
      const project = state.projects.find(
        (item) => item.id === action.payload.projectId,
      )
      if (!project) return

      const exists = project.boards.some(
        (board) => board.id === action.payload.board.id,
      )
      if (!exists) {
        project.boards.push(action.payload.board)
        project.boards = sortBoardsByPosition(project.boards)
      }
    },
    updateBoardTitle(
      state,
      action: PayloadAction<{
        projectId: number
        boardId: number
        title: string
      }>,
    ) {
      const project = state.projects.find(
        (item) => item.id === action.payload.projectId,
      )
      const board = project?.boards.find(
        (item) => item.id === action.payload.boardId,
      )
      if (board) {
        board.title = action.payload.title
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(removeBoard, (state, action) => {
        const project = state.projects.find(
          (item) => item.id === action.payload.projectId,
        )
        if (!project) return

        project.boards = project.boards.filter(
          (board) => board.id !== action.payload.boardId,
        )
      })
      .addCase(fetchProjectsThunk.pending, (state) => {
        state.loadingProjects = true
        state.projectsError = null
      })
      .addCase(fetchProjectsThunk.fulfilled, (state, action) => {
        state.loadingProjects = false
        state.projects = action.payload ?? []
        state.projectsError = null

        const hasActive = state.projects.some(
          (project) => project.id === state.activeProjectId,
        )
        if (!hasActive) {
          state.activeProjectId = null
        }
      })
      .addCase(fetchProjectsThunk.rejected, (state, action) => {
        state.loadingProjects = false
        state.projectsError =
          (action.payload as string) ?? 'Не удалось загрузить проекты'
      })
      .addCase(createProjectThunk.pending, (state) => {
        state.creatingProject = true
        state.createProjectError = null
      })
      .addCase(createProjectThunk.fulfilled, (state, action) => {
        state.creatingProject = false
        state.createProjectError = null

        if (!action.payload) return

        const {
          roles: _roles,
          members: _members,
          board: _board,
          ...project
        } = action.payload
        const existingIndex = state.projects.findIndex(
          (item) => item.id === project.id,
        )

        if (existingIndex >= 0) {
          state.projects[existingIndex] = project
        } else {
          state.projects.unshift(project)
        }

        state.activeProjectId = project.id
      })
      .addCase(createProjectThunk.rejected, (state, action) => {
        state.creatingProject = false
        state.createProjectError =
          (action.payload as string) ?? 'Не удалось создать проект'
      })
      .addCase(reorderProjectsThunk.pending, (state, action) => {
        state.reorderingProjects = true
        state.projects = action.meta.arg.reorderedProjects
      })
      .addCase(reorderProjectsThunk.fulfilled, (state, action) => {
        state.reorderingProjects = false
        state.projects =
          action.payload.updatedProjects ?? action.payload.reorderedProjects
      })
      .addCase(reorderProjectsThunk.rejected, (state, action) => {
        state.reorderingProjects = false
        const payload = action.payload as { previousProjects: TaskTrackerProject[] }
        if (payload?.previousProjects) {
          state.projects = payload.previousProjects
        }
      })
      .addCase(reorderProjectBoardsThunk.pending, (state, action) => {
        state.reorderingBoardsProjectId = action.meta.arg.projectId
        const project = state.projects.find(
          (item) => item.id === action.meta.arg.projectId,
        )
        if (project) {
          project.boards = action.meta.arg.reorderedBoards
        }
      })
      .addCase(reorderProjectBoardsThunk.fulfilled, (state, action) => {
        state.reorderingBoardsProjectId = null
        const project = state.projects.find(
          (item) => item.id === action.payload.projectId,
        )
        if (project) {
          project.boards = sortBoardsByPosition(
            action.payload.updatedBoards ?? action.payload.reorderedBoards,
          )
        }
      })
      .addCase(reorderProjectBoardsThunk.rejected, (state, action) => {
        state.reorderingBoardsProjectId = null
        const payload = action.payload as {
          projectId?: number
          previousBoards?: TaskTrackerBoard[]
        }
        const project = state.projects.find(
          (item) => item.id === payload?.projectId,
        )
        if (project && payload?.previousBoards) {
          project.boards = payload.previousBoards
        }
      })
  },
})

export const {
  setActiveProjectId,
  clearCreateProjectError,
  updateProjectTitle,
  removeProject,
  addProjectBoard,
  updateBoardTitle,
} = projectsSlice.actions

export default projectsSlice.reducer
